'use client';

import { ReactNode, useEffect, useState } from 'react';

import { client } from '@/sanity/lib/client';
import {
  createProviderFlags,
  FEATURE_FLAGS_QUERY,
  type FeatureFlag,
} from '@/sanity/lib/featureFlags.shared';

import { FeatureProvider } from './FeatureFlagContext';

const featureFlagsClient = client.withConfig({ useCdn: false });

const FEATURE_FLAGS_LISTEN_QUERY = `*[_type == "featureToggle"]`;

type FeatureFlagsProviderProps = {
  initialFeatures: string[];
  /** GrowthBook keys to re-merge after Sanity client sync (so GB flags are not wiped). */
  growthBookFeatures?: string[];
  enableClientSync: boolean;
  children: ReactNode;
};

export function FeatureFlagsProvider({
  initialFeatures,
  growthBookFeatures = [],
  enableClientSync,
  children,
}: FeatureFlagsProviderProps) {
  // Uses initialFeatures on first mount and updates with client sync if enabled
  const [featureFlags, setFeatureFlags] = useState<string[]>(initialFeatures);

  useEffect(() => {
    if (!enableClientSync) {
      return;
    }

    const mergeWithGrowthBook = (sanityKeys: string[]) => [
      ...new Set([...sanityKeys, ...growthBookFeatures]),
    ];

    const fetchFeatureFlags = () => {
      featureFlagsClient
        .fetch<FeatureFlag[]>(FEATURE_FLAGS_QUERY)
        .then((flags) => {
          setFeatureFlags(mergeWithGrowthBook(createProviderFlags(flags)));
        })
        .catch((error) => {
          console.error('Failed to fetch feature flags', error);
          // Keep GrowthBook keys even if Sanity fetch fails
          setFeatureFlags(mergeWithGrowthBook([]));
        });
    };

    fetchFeatureFlags();

    const subscription = client
      .listen(FEATURE_FLAGS_LISTEN_QUERY)
      .subscribe(() => fetchFeatureFlags());

    return () => {
      subscription.unsubscribe();
    };
  }, [enableClientSync, growthBookFeatures]);

  return <FeatureProvider features={featureFlags}>{children}</FeatureProvider>;
}
